import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import publicPages from '../PublicPages';
import { isSharedReportPath, isSignupPath } from '../publicPaths';
import { recordFailedRequest, trackFailedRequests } from './problemReport/failedRequests';

const excludedUrls = [
  '/refresh-token',
  '/logout',
  'login',
  '/forgot-password',
  '/complete-temporary-password-change',
];


axios.defaults.baseURL =
  (window as any).RUNTIME_ENV?.BACKEND_URL ??
  process.env.BACKEND_URL ??
  'http://localhost:8000/api/v1';
axios.defaults.withCredentials = true;

// The seed survives only when runtime-config.js did not load.
if ((window as any).RUNTIME_ENV?.BACKEND_URL_SEEDED) {
  console.warn(
    '[api] runtime-config.js did not load. Falling back to this origin:',
    axios.defaults.baseURL
  );
}

try {
  const clinicTz =
    (window as any).RUNTIME_ENV?.CLINIC_TIMEZONE ?? 'Africa/Casablanca';
  axios.defaults.headers.common['X-Client-Timezone'] = clinicTz;
} catch {
  axios.defaults.headers.common['X-Client-Timezone'] = 'Africa/Casablanca';
}

// Facility selector: forward the chosen facility so the backend narrows lists to
// it. Read from localStorage here to avoid a circular import with the helper.
axios.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('currentScope');
    const scope = raw ? JSON.parse(raw) : null;
    // Only a facility scope narrows lists; an org scope sends no header so the
    // backend returns the user's full ACL scope.
    if (scope?.kind === 'facility' && scope.id) {
      config.headers = config.headers || ({} as any);
      (config.headers as any)['X-Facility-Id'] = scope.id;
    }
  } catch {
    /* ignore */
  }
  return config;
});

interface ErrorResponse {
  response?: {
    status?: number;
    data?: {
      detail?: string;
    };
  };
  config?: AxiosRequestConfig;
}

interface RequestParams {
  [key: string]: string | number | boolean;
}

interface UltrasoundMachine {
  acronyme: string;
  [key: string]: unknown;
}

interface APIErrorDetails {
  sop_instance_uid: string;
  original_study_info: string;
}

interface APIErrorResponse {
  message: string;
  status_code: number;
  error: string;
  details: APIErrorDetails;
}

export const handleLogout = async () => {
  apiClient.post('/logout').then(() => {
    window.location.reload();
  });
};

axios.interceptors.response.use(
  async function (response: AxiosResponse) {
    return response.data ? response.data : response;
  },
  async function (error: ErrorResponse) {
    console.log('============ error', error);
    recordFailedRequest(error.config, error.response as any);
    const originalRequest = error.config;
    // Handle 401 and refresh token
    if (error?.response?.status === 401) {
      if (
        !originalRequest._retry &&
        !excludedUrls.includes(originalRequest.url)
      ) {
        originalRequest._retry = true;
        try {
          await apiClient.post('/refresh-token');
        } catch (err) {
          // Check if the current page is a public page before redirecting.
          // A patient opening a share link has no session and never will, so
          // sending them to /login would lose the report they were sent.
          const currentPath = window.location.pathname;
          const isPublicPage =
            publicPages.some(page => page.path === currentPath) ||
            isSharedReportPath(currentPath) ||
            isSignupPath(currentPath);
          if (!isPublicPage) {
            window.location.href = '/login';
          }
          return Promise.reject(err);
        }
        return axios(originalRequest);
      }
    }

    if (
      error?.response?.status === 403 &&
      error.response.data?.message === 'subscription_inactive' &&
      !isSignupPath(window.location.pathname)
    ) {
      window.location.href = '/signup';
    }

    // Return the full error response instead of just a message
    return Promise.reject(
      error.response?.data || {
        message: error.toString(),
        status_code: error.response?.status,
        error: 'UnknownError',
        details: {},
      }
    );
  }
);

// axios.interceptors.response.use(
//   async function (response: AxiosResponse) {
//     return response.data ? response.data : response;
//   },
//   async function (error: ErrorResponse) {
//     console.log('============ error', error);
//     let message: unknown;
//     const originalRequest = error.config;
//     switch (error?.response?.status) {
//       case 500:
//         message = 'Internal Server Error';
//         break;
//       case 401:
//         if (
//           !originalRequest._retry &&
//           !excludedUrls.includes(originalRequest.url)
//         ) {
//           originalRequest._retry = true;

//           await apiClient.post('/refresh-token2');
//           return axios(originalRequest);
//         }
//         message = 'Invalid credentials';
//         break;
//       case 404:
//         message = 'Sorry! the data you are looking for could not be found';
//         break;
//       default:
//         message = error?.response?.data || error.toString();
//     }
//     return Promise.reject(message);
//   }
// );

class APIClient {
  get = (url: string, paramsOrConfig?: RequestParams | AxiosRequestConfig) => {
    // If the second argument is an AxiosRequestConfig (has signal, headers, etc.) use it directly
    if (
      paramsOrConfig &&
      ('signal' in paramsOrConfig || 'headers' in paramsOrConfig || 'responseType' in paramsOrConfig)
    ) {
      return axios.get(url, paramsOrConfig as AxiosRequestConfig);
    }

    const params = paramsOrConfig as RequestParams | undefined;
    const paramKeys: string[] = [];

    if (params) {
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === undefined || value === null || value === '') {
          return;
        }
        // FastAPI List[str] expects repeated keys (?status=a&status=b), not CSV.
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== '') {
              paramKeys.push(`${key}=${encodeURIComponent(String(item))}`);
            }
          });
          return;
        }
        paramKeys.push(`${key}=${encodeURIComponent(String(value))}`);
      });

      const queryString = paramKeys.length ? paramKeys.join('&') : '';
      return axios.get(queryString ? `${url}?${queryString}` : url);
    }

    return axios.get(`${url}`);
  };
  getBlob = (url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<Blob>> => {
    return axios.get(url, {
      ...config,
      responseType: 'blob',
      withCredentials: true,
    });
  };

  downloadFile = (
    url: string,
    config: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<Blob>> => {
    // No response interceptor (blob must stay a Blob), but same API origin + cookies as the app.
    const instance = trackFailedRequests(axios.create({
      baseURL: axios.defaults.baseURL,
      withCredentials: true,
    }));

    return instance.get(url, {
      ...config,
      responseType: 'blob',
    });
  };
  fetchImageAsBase64DataUrl = async (url: string): Promise<string> => {
    try {
      const config: AxiosRequestConfig = {
        responseType: 'arraybuffer',
      };

      const response = await axios.get(url, config);
      const arrayBufferToBase64 = (arrayBuffer: ArrayBuffer): string => {
        let binary = '';
        const bytes = new Uint8Array(arrayBuffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
      };

      // Use response.data directly since we bypassed the interceptor
      const base64String = arrayBufferToBase64(response);
      return `data:image/jpeg;base64,${base64String}`;
    } catch (error) {
      console.error('Error fetching image:', error);
      throw error;
    }
  };

  getUltrasoundMachines = async (params = {}) => {
    // CHANGE: Added optional 'params' to support pagination, making it more flexible.
    const query = new URLSearchParams(params).toString();
    const endpoint = `/ultrasound_machines${query ? `?${query}` : ''}`;

    try {
      // The this.get method should handle the actual fetch call
      const response = await this.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching ultrasound machines:', error);
      throw error;
    }
  };
   createUltrasoundMachine = async (machineData) => {
    // CHANGE 1: The method now accepts a 'machineData' parameter. This is crucial.
    if (!machineData) {
      throw new Error('machineData payload is required to create a machine.');
    }

    try {
      // CHANGE 2: The 'machineData' is passed as the body of the POST request.
      // This assumes your `this.post` method is defined as `post(endpoint, body)`.
      const newMachine = await this.post('/ultrasound_machines', machineData);
      return newMachine;
    } catch (error) {
      // CHANGE 3: Corrected the error message for clarity.
      console.error('Error creating ultrasound machine:', error);
      throw error;
    }
  };
  addUltrasoundMachine = async (newRow: UltrasoundMachine) => {
    try {
      const response = await axios.post('/ultrasound_machine', newRow, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('Ultrasound Machine added successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error adding ultrasound machine:', error);
      throw error;
    }
  };

  updateUltrasoundMachine = async (updatedRow: UltrasoundMachine) => {
    try {
      const response = await axios.put(
        `/ultrasound_machines/${updatedRow.acronyme}`,
        updatedRow,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('Ultrasound Machine updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating ultrasound machine:', error);
      throw error;
    }
  };

  post = async (
    url: string,
    data: Record<string, unknown> = {},
    config: AxiosRequestConfig = {}
  ) => {
    // Ensure URL has trailing slash
    // const normalizedUrl = url.endsWith('/') ? url : `${url}/`; temp
    const normalizedUrl = url.endsWith('/') ? url : `${url}`; //temp for test i ll fix it
    console.log('======================= normalizedUrl', normalizedUrl);

    try {
      const response = await axios.post(normalizedUrl, data, {
        ...config,
        headers: {
          ...config.headers,
        },
      });
      console.log('============ POST request successful:', response.data);
      return response;
    } catch (error) {
      console.log('============ POST request failed:', error);
      throw error;
    }
  };
 postUpload = async (
  url: string,
  formData: FormData,
  config: AxiosRequestConfig = {}
) => {
  const normalizedUrl = url.endsWith('/') ? url : `${url}`;

  console.log('[postUpload] Uploading FormData');
  
  try {
    // Create instance without the problematic interceptor
    const uploadInstance = trackFailedRequests(axios.create({
      baseURL: axios.defaults.baseURL,
      withCredentials: axios.defaults.withCredentials,
      timeout: 300000,
    }));

    // Get auth token if it exists
    const authToken = axios.defaults.headers.common['Authorization'];

    const response = await uploadInstance.post(normalizedUrl, formData, {
      ...config,
      headers: {
        ...(authToken && { 'Authorization': authToken }),
        ...config.headers,
        // CRITICAL: Don't set Content-Type at all
        // The browser will automatically set it with the boundary
      },
    });
    
    console.log('============ POST Upload successful:', response.data);
    return response;
  } catch (error) {
    console.log('============ POST Upload failed:', error);
    throw error;
  }
};
  put = async (url: string, data: Record<string, unknown>, config: AxiosRequestConfig = {}) => {
    try {
      const response = await axios.put(url, data, { ...config });
      return response;
    } catch (error) {
      console.log('============ PUT request failed:', error);
      throw error;
    }
  };

  delete = (url: string, data?: unknown) => {
    return axios.delete(url);
  };

  patch = (url: string, data: Record<string, unknown>) => {
    return axios.patch(url, data);
  };
}

const apiClient = new APIClient();

export const apiBaseUrl = (): string => String(axios.defaults.baseURL ?? '').replace(/\/$/, '');

export { APIClient, apiClient };
